<script setup lang="ts">
// Support action: send an email to a participant again (RDT-38).
//
// The counterpart to the delivery status next to it (RDT-360) — once it is clear why an email did not
// arrive, this is what support does about it, without impersonating the trainer. The email is rebuilt
// by the backend from the project's current state, so a corrected address goes to the corrected one.
//
// Which email makes sense depends on where the participant stands, so the two actions enable
// separately; the backend refuses the same combinations and that refusal is reported on its own.
import { computed, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { ProfileAdministrationAPI } from "../../api";
import { Participant, ResendableEmail } from "../../types/PersologOnlineTypes.ts";

const props = defineProps({
    participant: {
        type: Object as PropType<Participant>,
        required: true,
    },
});

const emit = defineEmits<{ resent: [participant: Participant] }>();

const { t } = useI18n();
const confirm = useConfirm();
const toast = useToast();

const loading = ref(false);
const menu = ref();

/** Mirrors the backend's guard: an invitation only helps while the questionnaire is still open. */
const QUESTIONNAIRE_STATUSES = ["ACTIVATED", "STARTED"];
/** And the profile email needs a profile to link to. */
const PROFILE_STATUSES = ["GENERATED", "NOTIFIED"];

const status = computed(() => props.participant.status ?? "");

const participantName = computed(() => {
    const name = `${props.participant.firstName ?? ""} ${props.participant.name ?? ""}`.trim();
    return name || props.participant.email;
});

const options = computed(() => [
    {
        label: t("participants.resend.questionnaire"),
        icon: "pi pi-file-edit",
        disabled: !QUESTIONNAIRE_STATUSES.includes(status.value),
        command: () => askForConfirmation(ResendableEmail.QUESTIONNAIRE),
    },
    {
        label: t("participants.resend.profile"),
        icon: "pi pi-file-pdf",
        disabled: !PROFILE_STATUSES.includes(status.value),
        command: () => askForConfirmation(ResendableEmail.PROFILE),
    },
]);

const disabled = computed(() => !props.participant.id || options.value.every((option) => option.disabled));

const confirmGroup = computed(() => `resend-email-${props.participant.id}`);

const resend = async (type: ResendableEmail) => {
    const participantId = props.participant.id;
    if (!participantId) return;

    loading.value = true;
    try {
        const updated = await ProfileAdministrationAPI.resendEmail(participantId, type);
        toast.add({
            severity: "success",
            summary: t("messages.success"),
            detail: t("participants.resend.success", { participant: participantName.value }),
            life: 4000,
        });
        emit("resent", updated);
    } catch (error) {
        console.error("Could not resend the email", error);
        // 409 is the participant's status, 502 is the e-mail-service — worth telling apart, because
        // only the second one is worth retrying.
        const status = (error as { response?: { status?: number } })?.response?.status;
        const detail =
            status === 409
                ? t("participants.resend.notAllowed")
                : status === 502
                  ? t("participants.resend.serviceError")
                  : t("participants.resend.error");
        toast.add({ severity: "error", summary: t("messages.error"), detail, life: 5000 });
    } finally {
        loading.value = false;
    }
};

const askForConfirmation = (type: ResendableEmail) => {
    confirm.require({
        group: confirmGroup.value,
        header: t("participants.resend.confirm.header"),
        message: t(`participants.resend.confirm.${type.toLowerCase()}`, {
            participant: participantName.value,
            email: props.participant.email,
        }),
        icon: "pi pi-envelope",
        rejectProps: { label: t("buttons.cancel"), severity: "secondary", outlined: true },
        acceptProps: { label: t("participants.resend.action") },
        accept: () => resend(type),
    });
};
</script>

<template>
    <ConfirmDialog :group="confirmGroup" />
    <!-- A menu rather than a split button: neither of the two emails is the obvious default, and
         which one applies depends on the participant's status. -->
    <Menu :id="`resend-email-menu-${participant.id}`" ref="menu" :model="options" :popup="true" />
    <Button
        :data-cy="`resend-email-button-${participant.id}`"
        icon="pi pi-envelope"
        rounded
        outlined
        severity="secondary"
        class="mr-2"
        :aria-label="t('participants.resend.action')"
        :aria-haspopup="true"
        :aria-controls="`resend-email-menu-${participant.id}`"
        v-tooltip="t('participants.resend.action')"
        :loading="loading"
        :disabled="disabled"
        @click="menu.toggle($event)"
    />
</template>
